import React, { useState, useEffect } from "react";
import { T } from "./theme";
import { Card, Btn, Input } from "./ui";
import { REGULAMENTO as DEFAULT_REG } from "./regulamentoData";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// REGULAMENTO EDIT — admin/proprietário editam texto do regulamento
// Persiste em configuracoes.chave='regulamento' (JSON)
// ═══════════════════════════════════════════════════════════════════════

export default function RegulamentoEdit() {
  const [reg, setReg] = useState(DEFAULT_REG);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!isSupabaseReady) { setCarregando(false); return; }
    (async () => {
      const { data } = await supabase.from("configuracoes").select("valor").eq("chave", "regulamento").maybeSingle();
      if (data?.valor) {
        try {
          const v = typeof data.valor === "string" ? JSON.parse(data.valor) : data.valor;
          if (v && v.secoes) setReg(v);
        } catch {}
      }
      setCarregando(false);
    })();
  }, []);

  function setSecao(idx, campo, valor) {
    setReg(prev => {
      const secoes = [...prev.secoes];
      secoes[idx] = { ...secoes[idx], [campo]: valor };
      return { ...prev, secoes };
    });
  }
  function setConteudoSecao(idx, texto) {
    // Converte texto multi-linha em array de parágrafos (split por linha em branco)
    const paragrafos = texto.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    setSecao(idx, "conteudo", paragrafos);
  }
  function adicionarSecao() {
    setReg(prev => ({ ...prev, secoes: [...prev.secoes, { titulo: "Nova Seção", conteudo: [""] }] }));
  }
  function removerSecao(idx) {
    if (!window.confirm("Remover esta seção?")) return;
    setReg(prev => ({ ...prev, secoes: prev.secoes.filter((_, i) => i !== idx) }));
  }
  function moverSecao(idx, delta) {
    setReg(prev => {
      const secoes = [...prev.secoes];
      const novo = idx + delta;
      if (novo < 0 || novo >= secoes.length) return prev;
      [secoes[idx], secoes[novo]] = [secoes[novo], secoes[idx]];
      return { ...prev, secoes };
    });
  }

  async function salvar() {
    if (!isSupabaseReady) return;
    setSalvando(true);
    setMsg(null);
    try {
      const novo = { ...reg, versao: (parseFloat(reg.versao || "1.0") + 0.1).toFixed(1), vigenciaDesde: new Date().toISOString() };
      const { error } = await supabase
        .from("configuracoes")
        .upsert({ chave: "regulamento", valor: novo }, { onConflict: "chave" });
      if (error) throw error;
      setReg(novo);
      setMsg({ tipo: "ok", texto: `✅ Regulamento salvo · versão ${novo.versao}` });
    } catch (e) {
      setMsg({ tipo: "err", texto: "Erro: " + e.message });
    }
    setSalvando(false);
  }

  function restaurarPadrao() {
    if (!window.confirm("Restaurar regulamento padrão (descarta edições não salvas)?")) return;
    setReg(DEFAULT_REG);
  }

  if (carregando) return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Carregando...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 12, color: T.textSub }}>
        Edite as seções do regulamento. Salvar incrementa a versão automaticamente.
        Versão atual: <strong style={{ color: T.brand }}>{reg.versao}</strong>
      </div>

      {msg && (
        <div style={{
          padding: "10px 14px", borderRadius: T.r.md,
          background: msg.tipo === "ok" ? T.ok + "15" : T.err + "15",
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}44`,
          color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
        }}>{msg.texto}</div>
      )}

      {reg.secoes.map((s, idx) => (
        <Card key={idx}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: T.textDim, fontWeight: 700 }}>Seção {idx + 1}</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => moverSecao(idx, -1)} title="Mover acima" style={{ background: T.cardSoft, border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", color: T.textSub, fontSize: 11 }}>▲</button>
              <button onClick={() => moverSecao(idx, +1)} title="Mover abaixo" style={{ background: T.cardSoft, border: `1px solid ${T.border}`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", color: T.textSub, fontSize: 11 }}>▼</button>
              <button onClick={() => removerSecao(idx)} title="Remover" style={{ background: T.err + "22", border: `1px solid ${T.err}44`, borderRadius: 4, padding: "2px 8px", cursor: "pointer", color: T.err, fontSize: 11 }}>🗑️</button>
            </div>
          </div>
          <Input label="Título" value={s.titulo} onChange={e => setSecao(idx, "titulo", e.target.value)} />
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
              Conteúdo (separe parágrafos com linha em branco)
            </div>
            <textarea
              value={(s.conteudo || []).join("\n\n")}
              onChange={e => setConteudoSecao(idx, e.target.value)}
              rows={Math.max(6, (s.conteudo || []).join("\n\n").split("\n").length + 1)}
              style={{
                width: "100%", padding: 12, fontSize: 13, fontFamily: "inherit",
                background: T.surface, color: T.text,
                border: `1px solid ${T.border}`, borderRadius: T.r.md, resize: "vertical",
                lineHeight: 1.6,
              }}
            />
          </div>
        </Card>
      ))}

      <Btn variant="ghost" onClick={adicionarSecao} style={{ alignSelf: "flex-start", fontSize: 12 }}>+ Adicionar seção</Btn>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <Btn variant="ghost" onClick={restaurarPadrao} disabled={salvando}>Restaurar padrão</Btn>
        <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "💾 Salvar regulamento"}</Btn>
      </div>
    </div>
  );
}
